import { ControlValueAccessorValidatorBase } from '../control-value-accessor-validator-base';
import { IContactModel } from '../models/common';
import { ChangeDetectorRef, Directive, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FiUserSignInSettings } from '../../user/user-edit/fi-user-sign-in-settings';
import { isDefined, isEqual } from '../utils';
import { takeUntil } from 'rxjs/operators';
import { comparator } from '../utils/array-sort-comparators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DetectDevicesService } from '../fi-shared-services';
import { FiContactInformationTypes } from '../types/fi-contact-information-types';
import { removeItem } from '../utils/remove-item';
import { DataFieldPropertyType } from '../../data-field/data-field-property-type';
import { ContactService } from './contact.service';
import { getDefaultCountryId } from '../../core/country-id';
import { FiUserDataService } from '../fi-shared-services/fi-user-data.service';
import { IFiContactData } from './fi-contacts';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { FiUniqueContactsHelper } from '@fi-shared/fi-contacts/fi-unique-contacts-helper';

export interface IContactInformationType {
    Id: FiContactInformationTypes;
    Name: string;
}

@Directive()
export abstract class FiContactsBaseDirective
    extends ControlValueAccessorValidatorBase<IContactModel[]>
    implements OnInit, OnDestroy
{
    @Input() isRequired: boolean;
    @Input() fiUserSignInSettings: FiUserSignInSettings;
    @Input() isNewAddButtonLayout: boolean;
    @Input({ required: true }) contactData: IFiContactData;
    @Input() isSystemObject: boolean;

    @Output() onContactChange = new EventEmitter<IContactModel>();

    readonly contactTypeName: string = 'ContactType';
    availableContactTypes: IContactInformationType[] = [];

    detectDevicesService: DetectDevicesService = inject(DetectDevicesService);
    contactService: ContactService = inject(ContactService);
    fiContactsService: FiContactsService = inject(FiContactsService);
    userDataService: FiUserDataService = inject(FiUserDataService);
    cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    abstract dataFieldPropertyType: DataFieldPropertyType;
    abstract getContactTypesFn: () => FiContactInformationTypes[];
    abstract getContactTypeName: (contactType: FiContactInformationTypes) => string;
    abstract isContactSignInTypeFn: () => boolean;
    abstract getNextNewContactTypeFn: () => FiContactInformationTypes;

    fiUniqueContactsHelper: FiUniqueContactsHelper;

    ngOnInit() {
        this.sortContacts();
        this.setAvailableContactTypes();
        this.subscribeOnSignInTypeChange();
        this.fiUserSignInSettings?.updateSignInInputValidity$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            if (this.fiUserSignInSettings.isPrevSignInContactCurrentType(this.isPhoneNumberContact()))
                this.contactData.onContactValueChanged(this.fiUserSignInSettings.prevSignInContact);

            if (this.isSignInContactCurrentType()) {
                this.contactData.onContactValueChanged(this.fiUserSignInSettings.signInContact);
            }

            setTimeout(() => {
                this.cdr.markForCheck();
                this.fiUniqueContactsHelper?.setContacts(this.ngModel);
                this.fiUniqueContactsHelper?.setDuplicatesOnContactChange(this.fiUserSignInSettings.signInContact);
            });
        });
        this.setContactsUid();
        this._triggerUpdateContactValidity$.next(this.ngModel);
        this.fiUniqueContactsHelper = new FiUniqueContactsHelper(
            this.ngModel,
            this.fiContactsService.getContactValueForValidation,
        );
    }

    private setContactsUid() {
        this.ngModel = this.ngModel.map((contact) => {
            return contact.hasOwnProperty('Uid')
                ? contact
                : { ...contact, Uid: this.fiContactsService.getContactUid() };
        });
        this.doPropagateChanges();
    }

    sortContacts() {
        this.isContactSignInTypeFn() ? this.buildViewContactsBySignIn() : this.buildViewContactsByDefault();
    }

    setAvailableContactTypes() {
        this.availableContactTypes = [];
        this.getContactTypesFn().forEach((contactType: FiContactInformationTypes) => {
            this.availableContactTypes.push({
                Id: contactType,
                Name: this.getContactTypeName(contactType),
            });
        });
    }

    private subscribeOnSignInTypeChange(): void {
        this.fiUserSignInSettings?.signInTypeChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.cdr.markForCheck();
            this.sortContacts();
            if (isDefined(this.fiUserSignInSettings.prevSignInContact))
                this.contactData.onContactValueChanged(this.fiUserSignInSettings.prevSignInContact);
        });
    }

    onIsSignInSet(contact: IContactModel) {
        this.fiUserSignInSettings.setCurrentSignInContact(contact);
        this.onContactsChange();
    }

    onDefaultSet(defaultContact: IContactModel) {
        this.ngModel.forEach((contact: IContactModel) => {
            contact.IsDefault = isEqual(contact, defaultContact);
        });

        if (!this.isContactSignInTypeFn()) {
            this.buildViewContactsByDefault();
        }

        this.onContactsChange();
    }

    addNewContactAddress() {
        const newContactToAdd: IContactModel = {};
        newContactToAdd.ContactType = this.getNextNewContactTypeFn();
        newContactToAdd.ContactValue = '';
        if (this.isPhoneNumberContact()) {
            newContactToAdd.CountryId = getDefaultCountryId(this.userDataService.applicationLanguage);
        }
        newContactToAdd.Uid = this.fiContactsService.getContactUid();
        this.ngModel.push(newContactToAdd);
        this.onContactsChange();
    }

    contactChange(contact: IContactModel, contactIndex: number) {
        this.ngModel[contactIndex] = contact;
        this.setSignInContactStatus(contact);
        this.onContactChange.emit(contact);
        this.onContactsChange();
        this.fiUniqueContactsHelper.setDuplicatesOnContactChange(contact);
        this.handleSignInContactChange(contact);
    }

    private handleSignInContactChange(changedContact: IContactModel) {
        if (!changedContact.IsSignIn) return;

        if (this.fiUserSignInSettings.isPhoneNumberSignInType()) {
            this.fiUserSignInSettings.onPhoneNumberContactChange(changedContact);
        } else {
            this.fiUserSignInSettings.onLoginNameChanged(changedContact);
        }
    }

    private setSignInContactStatus(contact: IContactModel) {
        if (this.isSingleContactSignInType()) this.fiUserSignInSettings.setCurrentSignInContact(contact);
    }

    private isSingleContactSignInType(): boolean {
        return this.fiUserSignInSettings && this.isSignInContactCurrentType() && this.ngModel.length === 1;
    }

    removeContact(contact: IContactModel) {
        removeItem(this.ngModel, contact);
        this.onContactsChange();
        this.fiUniqueContactsHelper.setDuplicatesOnContactChange(contact);
    }

    private _triggerUpdateContactValidity$ = new BehaviorSubject<IContactModel[]>([]);
    get onUpdateContactValidity$(): Observable<IContactModel[]> {
        return this._triggerUpdateContactValidity$.asObservable();
    }

    private onContactsChange() {
        this.doPropagateChanges();
        this.cdr.markForCheck();
        this._triggerUpdateContactValidity$.next(this.ngModel);
        this.fiUniqueContactsHelper.setContacts(this.ngModel);
    }

    buildViewContactsBySignIn() {
        this.ngModel.sort(comparator((emailContact) => (emailContact.IsSignIn ? -1 : 0)));
    }

    buildViewContactsByDefault() {
        this.ngModel.sort(comparator((emailContact) => (emailContact.IsDefault ? -1 : 0)));
    }

    isContactTypeChosen(emailType: FiContactInformationTypes): boolean {
        return this.ngModel.some((address: IContactModel) => address.ContactType === emailType);
    }

    isEditDeleteDisabled(contact: IContactModel): boolean {
        return this.contactData.isContactEditDeleteDisabled(contact);
    }

    isMainContact(contact: IContactModel): boolean {
        if (this.ngModel.length === 1) return true;

        if (this.isContactSignInTypeFn() && this.ngModel.some((c: IContactModel) => c.IsSignIn))
            return contact.IsSignIn;

        // in case of phone numbers there might be no default contact
        return contact.IsDefault || isEqual(this.ngModel[0], contact);
    }

    isContactRequired(contact: IContactModel): boolean {
        // if custom sign in allowed - sign in can be empty
        return (
            this.isRequired ||
            (this.fiUserSignInSettings && contact.IsSignIn && !this.fiUserSignInSettings.allowCustomSignIn())
        );
    }

    isMobileSmall(): boolean {
        return this.detectDevicesService.isMobileSmall();
    }

    private isPhoneNumberContact(): boolean {
        return this.dataFieldPropertyType === DataFieldPropertyType.PhoneNumber;
    }

    private isSignInContactCurrentType(): boolean {
        return (
            (this.fiUserSignInSettings.isEmailSignInType() && !this.isPhoneNumberContact()) ||
            (this.fiUserSignInSettings.isPhoneNumberSignInType() && this.isPhoneNumberContact())
        );
    }

    protected ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
